import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  ArrowLeft,
  CheckCircle2,
  Eye,
  Loader2,
  PackageCheck,
  RefreshCw,
  Search,
  XCircle,
  RotateCcw,
} from "lucide-react";
import { toast } from "react-toastify";

import {
  approveAdminReturnRequest,
  listAdminReturnRequests,
  markAdminReturnReceived,
  rejectAdminReturnRequest,
  resetAdminOrderError,
  resetAdminOrderMeta,
  refundAdminReturnRequest,
} from "../../../actions/adminOrderActions";
import Meta from "../../../components/Meta";
import {
  formatDate,
  formatMoney,
  humanize,
  safeText,
} from "../orderViewUtils";
import { getSafeUserMessage } from "../../../utils/errorMessages";

import "./AdminOrdersScreen.css";
import OrderFinancialDialog from "./OrderFinancialDialog";
import ReturnQuantityDialog from "./ReturnQuantityDialog";
import { hasFinancialPermission } from "./AdminOrderFinancialActions";

const PAGE_SIZE = 20;

const RETURN_STATUSES = [
  "",
  "requested",
  "under_review",
  "approved",
  "awaiting_customer_shipment",
  "in_transit",
  "received",
  "inspecting",
  "refund_pending",
  "refund_approved",
  "partially_refunded",
  "refunded",
  "rejected",
  "cancelled",
  "closed",
];

const APPROVABLE_STATUSES = new Set(["requested", "under_review"]);
const RECEIVABLE_STATUSES = new Set([
  "approved",
  "awaiting_customer_shipment",
  "in_transit",
  "received",
  "partially_refunded",
  "refunded",
]);
const CLOSED_STATUSES = new Set(["rejected", "refunded", "cancelled", "closed"]);

function getPageParam(searchParams) {
  const page = Number(searchParams.get("page") || 1);
  return Number.isFinite(page) && page > 0 ? Math.floor(page) : 1;
}

function getReturnId(returnRequest = {}) {
  return safeText(returnRequest.id || returnRequest._id);
}

function getReturnNumber(returnRequest = {}) {
  return safeText(returnRequest.returnNumber || getReturnId(returnRequest));
}

function getStatusTone(status = "") {
  const value = safeText(status).toLowerCase();

  if (["approved", "received", "refunded"].includes(value)) return "success";
  if (
    [
      "under_review",
      "awaiting_customer_shipment",
      "in_transit",
      "inspecting",
      "refund_pending",
      "refund_approved",
      "partially_refunded",
    ].includes(value)
  ) {
    return "info";
  }
  if (["requested"].includes(value)) return "warning";
  if (["rejected", "cancelled", "closed"].includes(value)) return "danger";

  return "neutral";
}

function StatusBadge({ value }) {
  return (
    <span className={`admin-orders__badge is-${getStatusTone(value)}`}>
      {humanize(value, "Requested")}
    </span>
  );
}

function getCustomerName(returnRequest = {}) {
  return safeText(returnRequest.customer?.name || returnRequest.user?.name);
}

function getCustomerEmail(returnRequest = {}) {
  return safeText(returnRequest.customer?.email || returnRequest.user?.email);
}

function ReturnRow({
  actionBusyId,
  onApprove,
  onMarkReceived,
  onReject,
  onRefund,
  financialPermission,
  returnRequest,
}) {
  const returnId = getReturnId(returnRequest);
  const returnNumber = getReturnNumber(returnRequest);
  const orderNumber = safeText(returnRequest.orderNumber);
  const status = safeText(returnRequest.status).toLowerCase();
  const currency = safeText(returnRequest.currency || "GBP");
  const estimatedTotal = Number(returnRequest.estimatedRefundTotal || 0);
  const approvedTotal = Number(returnRequest.approvedRefundTotal || 0);
  const refundedTotal = Number(returnRequest.refundedTotal || 0);
  const isBusy = actionBusyId === returnId;
  const isActionDisabled = Boolean(actionBusyId);

  return (
    <tr>
      <td data-label="Return">
        <strong>{returnNumber}</strong>
        <small>{formatDate(returnRequest.createdAt)}</small>
      </td>
      <td data-label="Order">
        {orderNumber ? (
          <Link
            className="admin-orders__order-link"
            to={`/admin/orders/${encodeURIComponent(orderNumber)}`}
          >
            {orderNumber}
          </Link>
        ) : (
          <strong>Order</strong>
        )}
        <small>{returnRequest.reasonLabel || humanize(returnRequest.reasonCategory, "Reason")}</small>
      </td>
      <td data-label="Customer">
        <strong>{getCustomerName(returnRequest) || "Customer"}</strong>
        <small>{getCustomerEmail(returnRequest) || "No email"}</small>
      </td>
      <td data-label="Status">
        <StatusBadge value={status} />
        <small>
          {returnRequest.kind === "statutory_cancellation" ? "Statutory cancellation" : returnRequest.containsFaultClaim ? "Fault claim" : "Standard return"}
        </small>
      </td>
      <td data-label="Items">
        <strong>{Number(returnRequest.distinctItemCount || 0)}</strong>
        <small>{Number(returnRequest.itemCount || 0)} item(s)</small>
        <details><summary>Item details and notes</summary>
          {returnRequest.items?.map((item) => <p key={item.lineId}>
            <strong>{item.productName}</strong> {item.variantName || item.variantType} {item.sku || item.variantSku}<br />
            Requested {item.qty}; approved {item.approvedQty}; posted {item.postedQty || 0}; received {item.receivedQty}; refunded {item.refundedQty}.<br />
            {item.reasonNote}
          </p>)}
          <p>{returnRequest.reasonNote} {returnRequest.customerMessage}</p>
          {returnRequest.policySnapshot?.eligibility?.lines?.some((line) => line.exemptionReviewRequired) ? <p role="status">Product exemption classification requires legal review. Statutory cancellation remains available.</p> : null}
          {returnRequest.noticeReceivedAt ? <p>Notice: {formatDate(returnRequest.noticeReceivedAt)}</p> : null}
          {returnRequest.returnPostingDeadline ? <p>Post by: {formatDate(returnRequest.returnPostingDeadline)}</p> : null}
          {returnRequest.refundDueAt ? <p>Next refund due: {formatDate(returnRequest.refundDueAt)}</p> : null}
          {returnRequest.postage?.evidence ? <p>Proof of return: {returnRequest.postage.evidence} {returnRequest.postage.trackingNumber}</p> : null}
        </details>
      </td>
      <td data-label="Refund">
        <strong>{formatMoney(estimatedTotal, currency)}</strong>
        <small>
          Approved {formatMoney(approvedTotal, currency)} · Refunded{" "}
          {formatMoney(refundedTotal, currency)}
        </small>
      </td>
      <td data-label="Actions">
        <div className="admin-orders__actions">
          {financialPermission && returnRequest.refundEligibility?.refundable ? (
            <button type="button" className="admin-orders__ghost-btn" disabled={isActionDisabled} onClick={() => onRefund(returnRequest)}>
              <RotateCcw size={15} /><span>Refund</span>
            </button>
          ) : null}
          {orderNumber ? (
            <Link
              className="admin-orders__ghost-btn"
              to={`/admin/orders/${encodeURIComponent(orderNumber)}`}
            >
              <Eye size={15} />
              <span>Order</span>
            </Link>
          ) : null}
          {APPROVABLE_STATUSES.has(status) ? (
            <button
              type="button"
              className="admin-orders__ghost-btn"
              disabled={isActionDisabled}
              onClick={() => onApprove(returnRequest)}
            >
              {isBusy ? (
                <Loader2 size={15} className="is-spinning" />
              ) : (
                <CheckCircle2 size={15} />
              )}
              <span>Approve</span>
            </button>
          ) : null}
          {RECEIVABLE_STATUSES.has(status) ? (
            <button
              type="button"
              className="admin-orders__ghost-btn"
              disabled={isActionDisabled}
              onClick={() => onMarkReceived(returnRequest)}
            >
              {isBusy ? (
                <Loader2 size={15} className="is-spinning" />
              ) : (
                <PackageCheck size={15} />
              )}
              <span>Received</span>
            </button>
          ) : null}
          {APPROVABLE_STATUSES.has(status) && returnRequest.kind !== "statutory_cancellation" ? (
            <button
              type="button"
              className="admin-orders__ghost-btn"
              disabled={isActionDisabled}
              onClick={() => onReject(returnRequest)}
            >
              {isBusy ? (
                <Loader2 size={15} className="is-spinning" />
              ) : (
                <XCircle size={15} />
              )}
              <span>Reject</span>
            </button>
          ) : null}
        </div>
      </td>
    </tr>
  );
}

export default function AdminReturnsScreen() {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.userAuth?.userInfo || {});
  const financialPermission = hasFinancialPermission(user);
  const [refundRequest, setRefundRequest] = useState(null);
  const [quantityDialog, setQuantityDialog] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const listState = useSelector((state) => state.adminOrder?.returnList || {});
  const returnRequests = Array.isArray(listState.returnRequests)
    ? listState.returnRequests
    : [];

  const page = getPageParam(searchParams);
  const [orderNumber, setOrderNumber] = useState(
    searchParams.get("orderNumber") || "",
  );
  const [status, setStatus] = useState(searchParams.get("status") || "");
  const [actionBusyId, setActionBusyId] = useState("");

  const totalPages = Math.max(1, Number(listState.pages || 1));

  const query = useMemo(
    () => ({
      page,
      limit: PAGE_SIZE,
      ...(safeText(searchParams.get("orderNumber"))
        ? { orderNumber: safeText(searchParams.get("orderNumber")).toUpperCase() }
        : {}),
      ...(safeText(searchParams.get("status"))
        ? { status: safeText(searchParams.get("status")).toLowerCase() }
        : {}),
    }),
    [page, searchParams],
  );

  const loadReturns = useCallback(async () => {
    try {
      await dispatch(listAdminReturnRequests(query));
    } catch (error) {
      toast.error(
        getSafeUserMessage(
          error?.message,
          "We could not load returns right now. Please try again later.",
        ),
      );
    }
  }, [dispatch, query]);

  async function confirmRefund(payload) {
    const returnId = getReturnId(refundRequest);
    setActionBusyId(returnId);
    try {
      const data = await dispatch(refundAdminReturnRequest({ returnRequestId: returnId, ...payload }));
      toast.success([data.message, humanize(data.refundAction?.status), data.refundAction?.providerRefundId].filter(Boolean).join(" · "));
    } finally {
      await loadReturns();
      setActionBusyId("");
    }
  }

  useEffect(() => {
    loadReturns();

    return () => {
      dispatch(resetAdminOrderError());
      dispatch(resetAdminOrderMeta());
    };
  }, [dispatch, loadReturns]);

  const applyFilters = useCallback(() => {
    const next = new URLSearchParams();
    const cleanOrderNumber = safeText(orderNumber).toUpperCase();

    if (cleanOrderNumber) next.set("orderNumber", cleanOrderNumber);
    if (status) next.set("status", status);
    next.set("page", "1");

    setSearchParams(next, { replace: true });
  }, [orderNumber, setSearchParams, status]);

  const setPage = useCallback(
    (nextPage) => {
      const next = new URLSearchParams(searchParams);
      next.set("page", String(Math.max(1, Math.min(totalPages, nextPage))));
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams, totalPages],
  );

  const openCount = returnRequests.filter(
    (request) => !CLOSED_STATUSES.has(safeText(request.status).toLowerCase()),
  ).length;
  const faultCount = returnRequests.filter((request) =>
    Boolean(request.containsFaultClaim),
  ).length;
  const refundTotal = returnRequests.reduce(
    (total, request) => total + Number(request.estimatedRefundTotal || 0),
    0,
  );

  return (
    <main className="admin-orders">
      <Meta
        title="Admin returns | Academy Costumes London"
        description="Manage Academy Costumes London return requests."
        robots="noindex, nofollow"
      />

      <header className="admin-orders__hero">
        <div>
          <p className="admin-orders__eyebrow">Operations</p>
          <h1>Returns</h1>
          <p className="admin-orders__subtitle">
            Review customer return requests, approve checks, mark received items
            and keep return handling aligned with order fulfilment.
          </p>
        </div>

        <div className="admin-orders__hero-actions">
          <Link className="admin-orders__ghost-btn" to="/admin/orders">
            <ArrowLeft size={16} />
            <span>Orders</span>
          </Link>
          <button
            type="button"
            className="admin-orders__dark-btn"
            onClick={loadReturns}
            disabled={Boolean(listState.loading)}
          >
            {listState.loading ? (
              <Loader2 size={16} className="is-spinning" />
            ) : (
              <RefreshCw size={16} />
            )}
            <span>Refresh</span>
          </button>
        </div>
      </header>

      <section className="admin-orders__stats">
        <div className="admin-orders__stat-card">
          <span>Total</span>
          <strong>{Number(listState.count || returnRequests.length)}</strong>
          <small>Matching returns</small>
        </div>
        <div className="admin-orders__stat-card">
          <span>Open</span>
          <strong>{openCount}</strong>
          <small>loaded on this page</small>
        </div>
        <div className="admin-orders__stat-card">
          <span>Fault claims</span>
          <strong>{faultCount}</strong>
          <small>loaded on this page</small>
        </div>
        <div className="admin-orders__stat-card">
          <span>Estimated</span>
          <strong>{formatMoney(refundTotal, "GBP")}</strong>
          <small>loaded refund total</small>
        </div>
      </section>

      <section className="admin-orders__panel">
        <div className="admin-orders__toolbar">
          <label className="admin-orders__search">
            <Search size={18} />
            <input
              value={orderNumber}
              onChange={(event) => setOrderNumber(event.target.value)}
              placeholder="Order number"
            />
          </label>

          <div className="admin-orders__filter-grid">
            <label>
              <span>Status</span>
              <select
                value={status}
                onChange={(event) => setStatus(event.target.value)}
              >
                {RETURN_STATUSES.map((item) => (
                  <option key={item || "all"} value={item}>
                    {item ? humanize(item) : "All statuses"}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="admin-orders__toolbar-actions">
            <button
              type="button"
              className="admin-orders__dark-btn"
              onClick={applyFilters}
            >
              Apply
            </button>
          </div>
        </div>

        {listState.loading && !returnRequests.length ? (
          <div className="admin-orders__loader">
            <Loader2 className="is-spinning" />
          </div>
        ) : returnRequests.length ? (
          <div className="admin-orders__table-wrap">
            <table className="admin-orders__table">
              <thead>
                <tr>
                  <th>Return</th>
                  <th>Order</th>
                  <th>Customer</th>
                  <th>Status</th>
                  <th>Items</th>
                  <th>Refund</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {returnRequests.map((returnRequest) => (
                  <ReturnRow
                    key={getReturnId(returnRequest)}
                    actionBusyId={actionBusyId}
                    onApprove={(request) => setQuantityDialog({ request, action: "approve" })}
                    onMarkReceived={(request) => setQuantityDialog({ request, action: "receive" })}
                    onReject={(request) => setQuantityDialog({ request, action: "reject" })}
                    onRefund={setRefundRequest}
                    financialPermission={financialPermission}
                    returnRequest={returnRequest}
                  />
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="admin-orders__loader">
            <PackageCheck size={32} />
            <p>{listState.error || "No return requests match these filters."}</p>
          </div>
        )}

        <footer className="admin-orders__pagination">
          <span>
            Page {page} of {totalPages}
          </span>
          <div className="admin-orders__pagination-actions">
            <button
              type="button"
              onClick={() => setPage(page - 1)}
              disabled={page <= 1 || Boolean(listState.loading)}
            >
              Previous
            </button>
            <button
              type="button"
              onClick={() => setPage(page + 1)}
              disabled={page >= totalPages || Boolean(listState.loading)}
            >
              Next
            </button>
          </div>
        </footer>
      </section>
      {refundRequest ? <OrderFinancialDialog action="refund" amount={refundRequest.refundEligibility.amount}
        currency={refundRequest.currency} provider={refundRequest.refundEligibility.provider}
        onClose={() => setRefundRequest(null)} onConfirm={confirmRefund} /> : null}
      {quantityDialog ? <ReturnQuantityDialog {...quantityDialog} onClose={() => setQuantityDialog(null)} onConfirm={async (payload) => {
        const action = { approve: approveAdminReturnRequest, receive: markAdminReturnReceived, reject: rejectAdminReturnRequest }[quantityDialog.action];
        setActionBusyId(getReturnId(quantityDialog.request));
        try {
          await dispatch(action({ returnRequestId: getReturnId(quantityDialog.request), ...payload }));
          toast.success("Return updated."); await loadReturns();
        } finally { setActionBusyId(""); }
      }} /> : null}
    </main>
  );
}
